import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import useMessages from '../../hooks/useMessages';
import TimelapseIcon from '@material-ui/icons/Timelapse';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '24px',
    background: '#f5f7fa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  headerLeft: {
    flex: 1,
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: 0,
  },
  filterContainer: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  filterInput: {
    padding: '8px 12px',
    border: '1px solid #e8ecf1',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
    '&:focus': {
      outline: 'none',
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  filterButton: {
    padding: '8px 16px',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: '#2563eb',
    },
  },
  tabsContainer: {
    display: 'flex',
    gap: '8px',
    borderBottom: '2px solid #e8ecf1',
    marginBottom: '24px',
    overflowX: 'auto',
  },
  tab: {
    padding: '12px 20px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    color: '#666',
    borderBottom: '3px solid transparent',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: '#1a1a1a',
    },
  },
  tabActive: {
    color: '#3b82f6',
    borderBottomColor: '#3b82f6',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e8ecf1',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      transform: 'translateY(-2px)',
    },
  },
  cardIcon: {
    fontSize: '20px',
    marginBottom: '6px',
    display: 'block',
  },
  cardLabel: {
    fontSize: '11px',
    color: '#666',
    fontWeight: 500,
    marginBottom: '6px',
    lineHeight: '1.3',
  },
  cardValue: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#1a1a1a',
  },
  chartsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '16px',
  },
  chartCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e8ecf1',
  },
  chartTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '16px',
  },
  barChart: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: '280px',
    gap: '16px',
    padding: '20px 10px',
  },
  barItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
    height: '100%',
    gap: '8px',
  },
  bar: {
    width: '100%',
    borderRadius: '6px 6px 0 0',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      opacity: 0.9,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
  },
  barLabel: {
    fontSize: '11px',
    color: '#666',
    textAlign: 'center',
    wordBreak: 'break-word',
    fontWeight: 500,
  },
  barValue: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1a1a1a',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    background: '#f9fafb',
    borderBottom: '2px solid #e8ecf1',
  },
  tableHeaderCell: {
    padding: '12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#666',
  },
  tableRow: {
    borderBottom: '1px solid #e8ecf1',
    '&:hover': {
      background: '#f9fafb',
    },
  },
  tableCell: {
    padding: '12px',
    fontSize: '13px',
    color: '#1a1a1a',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
  },
  badgeOnline: {
    background: '#d1fae5',
    color: '#065f46',
  },
  badgeOffline: {
    background: '#fee2e2',
    color: '#7f1d1d',
  },
  progressBar: {
    height: '8px',
    background: '#e8ecf1',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#666',
    marginBottom: '8px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#999',
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('indicators');
  const [dateFrom, setDateFrom] = useState(
    new Date(new Date().setDate(1)).toISOString().split('T')[0]
  );
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  const { count: countReceivedAll } = useMessages({ fromMe: false });
  const { count: countSentAll } = useMessages({ fromMe: true });
  const { count: countReceivedPeriod } = useMessages({ 
    fromMe: false, 
    dateStart: dateFrom, 
    dateEnd: dateTo 
  });
  const { count: countSentPeriod } = useMessages({ 
    fromMe: true, 
    dateStart: dateFrom, 
    dateEnd: dateTo 
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/dashboard', {
          params: {
            date_from: dateFrom,
            date_to: dateTo,
          }
        });
        
        const counters = data.counters || {};
        const attendants = data.attendants || [];
        
        setDashboardData({
          ...counters,
          attendants: attendants
        });
      } catch (error) {
        console.error('Erro ao buscar dados do dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>{i18n.t("dashboard.assessments.loading")}</div>
      </div>
    );
  }

  const data = dashboardData || {};

  const indicators = [
    { icon: '💬', label: i18n.t("dashboard.cards.inService"), value: data.supportHappening || 0 },
    { icon: <TimelapseIcon style={{ fontSize: 20 }} />, label: i18n.t("dashboard.cards.calling"), value: data.supportPending || 0 },
    { icon: '✓', label: i18n.t("dashboard.cards.finished"), value: data.supportFinished || 0 },
    { icon: '👥', label: i18n.t("dashboard.cards.groups"), value: data.supportGroups || 0 },
    { icon: '📋', label: i18n.t("dashboard.cards.newLeads"), value: data.leads || 0 },
    { icon: '📨', label: i18n.t("dashboard.cards.receivedMessages"), value: `${countReceivedPeriod}/${countReceivedAll}` },
    { icon: '📤', label: i18n.t("dashboard.cards.sentMessages"), value: `${countSentPeriod}/${countSentAll}` },
    { icon: '⏱️', label: i18n.t("dashboard.cards.avgServiceTime"), value: `${Math.ceil(data.avgSupportTime || 0)}m` },
    { icon: '⏳', label: i18n.t("dashboard.cards.avgWaitTime"), value: `${Math.ceil(data.avgWaitTime || 0)}m` },
    { icon: '🎫', label: i18n.t("dashboard.cards.activeTicketsCount"), value: data.activeTickets || 0 },
    { icon: '📦', label: i18n.t("dashboard.cards.passiveTicketsCount"), value: data.passiveTickets || 0 },
    { icon: '📊', label: i18n.t("dashboard.cards.totalTicketsCount"), value: data.tickets || 0 },
  ];

  const attendanceStats = [
    { icon: '💬', label: i18n.t("dashboard.assessments.totalCalls"), value: data.tickets || 0 },
    { icon: <TimelapseIcon style={{ fontSize: 20 }} />, label: i18n.t("dashboard.assessments.callsWaitRating"), value: data.waitRating || 0 },
    { icon: '⭐', label: i18n.t("dashboard.assessments.callsWithoutRating"), value: data.withoutRating || 0 },
    { icon: '✓', label: i18n.t("dashboard.assessments.ratedCalls"), value: data.withRating || 0 },
  ];

  const npsData = [
    { label: i18n.t("dashboard.assessments.prosecutors"), value: data.npsPromotersPerc || 0, color: '#10b981' },
    { label: i18n.t("dashboard.assessments.neutral"), value: data.npsPassivePerc || 0, color: '#f59e0b' },
    { label: i18n.t("dashboard.assessments.detractors"), value: data.npsDetractorsPerc || 0, color: '#ef4444' },
  ];

  const chartColors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#06b6d4', '#10b981'];

  const renderChart = (items, maxValue = null) => {
    const max = maxValue || Math.max(...items.map(item => item.value || 0), 1);
    
    return (
      <div className={classes.barChart}>
        {items.map((item, idx) => {
          const heightPercent = ((item.value || 0) / max) * 100;
          const height = Math.max(heightPercent, 20);
          
          return (
            <div key={idx} className={classes.barItem}>
              <div
                className={classes.bar}
                style={{
                  height: `${height}%`,
                  background: item.color || chartColors[idx % chartColors.length],
                }}
              />
              <div className={classes.barValue}>{item.value || 0}</div>
              <div className={classes.barLabel}>{item.label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <h1 className={classes.title}>📈 Dashboard</h1>
        </div>
        <div className={classes.filterContainer}>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={classes.filterInput}
            title={i18n.t("dashboard.date.initialDate")}
          />
          <span style={{ color: '#999' }}>{i18n.t("dashboard.date.until")}</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={classes.filterInput}
            title={i18n.t("dashboard.date.finalDate")}
          />
          <button
            className={classes.filterButton}
            onClick={() => {
              setLoading(true);
              setTimeout(() => setLoading(false), 500);
            }}
          >
            {i18n.t("dashboard.date.apply")}
          </button>
        </div>
      </div>

      <div className={classes.tabsContainer}>
        <button
          className={`${classes.tab} ${activeTab === 'indicators' ? classes.tabActive : ''}`}
          onClick={() => setActiveTab('indicators')}
        >
          📊 {i18n.t("dashboard.tabs.indicators")}
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'attendance' ? classes.tabActive : ''}`}
          onClick={() => setActiveTab('attendance')}
        >
          💬 {i18n.t("dashboard.tabs.attendance")}
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'attendants' ? classes.tabActive : ''}`}
          onClick={() => setActiveTab('attendants')}
        >
          👥 {i18n.t("dashboard.tabs.attendants")}
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'charts' ? classes.tabActive : ''}`}
          onClick={() => setActiveTab('charts')}
        >
          📈 {i18n.t("dashboard.tabs.charts")}
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'satisfaction' ? classes.tabActive : ''}`}
          onClick={() => setActiveTab('satisfaction')}
        >
          ⭐ {i18n.t("dashboard.tabs.satisfaction")}
        </button>
      </div>

      <div>
        {activeTab === 'indicators' && (
          <div className={classes.section}>
            <h2 className={classes.sectionTitle}>{i18n.t("dashboard.assessments.mainIndicators")}</h2>
            <div className={classes.grid}>
              {indicators.map((indicator, idx) => (
                <div key={idx} className={classes.card}>
                  <div className={classes.cardIcon}>{indicator.icon}</div>
                  <div className={classes.cardLabel}>{indicator.label}</div>
                  <div className={classes.cardValue}>{indicator.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'attendance' && (
          <div>
            <div className={classes.section}>
              <h2 className={classes.sectionTitle}>{i18n.t("dashboard.assessments.attendanceStatistics")}</h2>
              <div className={classes.grid}>
                {attendanceStats.map((stat, idx) => (
                  <div key={idx} className={classes.card}>
                    <div className={classes.cardIcon}>{stat.icon}</div>
                    <div className={classes.cardLabel}>{stat.label}</div>
                    <div className={classes.cardValue}>{stat.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.section}>
              <h2 className={classes.sectionTitle}>{i18n.t("dashboard.assessments.evaluationIndex")}</h2>
              <div className={classes.card} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', fontWeight: 700, color: '#f59e0b' }}>
                  {data.npsScore || 0}%
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'attendants' && (
          <div className={classes.section}>
            <h2 className={classes.sectionTitle}>{i18n.t("dashboard.assessments.attendants")}</h2>
            {data.attendants && data.attendants.length > 0 ? (
              <div className={classes.chartCard}>
                <table className={classes.table}>
                  <thead className={classes.tableHeader}>
                    <tr>
                      <th className={classes.tableHeaderCell}>{i18n.t("dashboard.users.name")}</th>
                      <th className={classes.tableHeaderCell}>{i18n.t("dashboard.users.score")}</th>
                      <th className={classes.tableHeaderCell}>{i18n.t("dashboard.users.ratedAttendances")}</th>
                      <th className={classes.tableHeaderCell}>{i18n.t("dashboard.users.totalAttendances")}</th>
                      <th className={classes.tableHeaderCell}>{i18n.t("dashboard.users.avgWaitTime")}</th>
                      <th className={classes.tableHeaderCell}>{i18n.t("dashboard.users.avgAttendanceTime")}</th>
                      <th className={classes.tableHeaderCell}>{i18n.t("dashboard.cards.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.attendants.map((attendant) => (
                      <tr key={attendant.id} className={classes.tableRow}>
                        <td className={classes.tableCell}>{attendant.name}</td>
                        <td className={classes.tableCell}>{attendant.score || 0}</td>
                        <td className={classes.tableCell}>{attendant.ratedAttendances || 0}</td>
                        <td className={classes.tableCell}>{attendant.totalAttendances || 0}</td>
                        <td className={classes.tableCell}>{typeof attendant.avgWaitTime === 'number' ? `${Math.ceil(attendant.avgWaitTime)}m` : (attendant.avgWaitTime || '00h 00m')}</td>
                        <td className={classes.tableCell}>{typeof attendant.avgAttendanceTime === 'number' ? `${Math.ceil(attendant.avgAttendanceTime)}m` : (attendant.avgAttendanceTime || '00h 00m')}</td>
                        <td className={classes.tableCell}>
                          <span
                            className={`${classes.badge} ${
                              attendant.online ? classes.badgeOnline : classes.badgeOffline
                            }`}
                          >
                            {attendant.online ? `🟢 ${i18n.t("dashboard.users.online")}` : `🔴 ${i18n.t("dashboard.users.offline")}`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={classes.emptyState}>{i18n.t("dashboard.assessments.noAttendantsAvailable")}</div>
            )}
          </div>
        )}

        {activeTab === 'charts' && (
          <div className={classes.section}>
            <h2 className={classes.sectionTitle}>{i18n.t("dashboard.assessments.analysisAndCharts")}</h2>
            <div className={classes.chartsContainer}>
              <div className={classes.chartCard}>
                <div className={classes.chartTitle}>{i18n.t("dashboard.charts.messagesReceivedVsSent")}</div>
                {renderChart([
                  { label: i18n.t("dashboard.cards.receivedMessages"), value: countReceivedPeriod || 0, color: '#10b981' },
                  { label: i18n.t("dashboard.cards.sentMessages"), value: countSentPeriod || 0, color: '#3b82f6' },
                ])}
              </div>

              <div className={classes.chartCard}>
                <div className={classes.chartTitle}>{i18n.t("dashboard.charts.ticketStatus")}</div>
                {renderChart([
                  { label: i18n.t("dashboard.cards.activeTicketsCount"), value: data.activeTickets || 0, color: '#3b82f6' },
                  { label: i18n.t("dashboard.cards.passiveTicketsCount"), value: data.passiveTickets || 0, color: '#f59e0b' },
                  { label: i18n.t("dashboard.cards.finished"), value: data.supportFinished || 0, color: '#10b981' },
                ])}
              </div>

              <div className={classes.chartCard}>
                <div className={classes.chartTitle}>{i18n.t("dashboard.charts.attendanceByStatus")}</div>
                {renderChart([
                  { label: i18n.t("dashboard.cards.inService"), value: data.supportHappening || 0, color: '#3b82f6' },
                  { label: i18n.t("dashboard.cards.waiting"), value: data.supportPending || 0, color: '#f59e0b' },
                  { label: i18n.t("dashboard.cards.finished"), value: data.supportFinished || 0, color: '#10b981' },
                ])}
              </div>

              <div className={classes.chartCard}>
                <div className={classes.chartTitle}>{i18n.t("dashboard.charts.newContactsVsGroups")}</div>
                {renderChart([
                  { label: i18n.t("dashboard.cards.newLeads"), value: data.leads || 0, color: '#8b5cf6' },
                  { label: i18n.t("dashboard.cards.groups"), value: data.supportGroups || 0, color: '#ec4899' },
                ])}
              </div>

              <div className={classes.chartCard}>
                <div className={classes.chartTitle}>{i18n.t("dashboard.charts.totalTickets")}</div>
                {renderChart([
                  { label: i18n.t("dashboard.cards.totalTicketsCount"), value: data.tickets || 0, color: '#3b82f6' },
                  { label: i18n.t("dashboard.cards.activeTicketsCount"), value: data.activeTickets || 0, color: '#10b981' },
                  { label: i18n.t("dashboard.cards.passiveTicketsCount"), value: data.passiveTickets || 0, color: '#f59e0b' },
                ])}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'satisfaction' && (
          <div className={classes.section}>
            <h2 className={classes.sectionTitle}>{i18n.t("dashboard.assessments.npsResearch")}</h2>
            <div className={classes.twoColumnGrid}>
              {npsData.map((nps, idx) => (
                <div key={idx} className={classes.chartCard}>
                  <div className={classes.chartTitle}>{nps.label}</div>
                  <div className={classes.progressLabel}>
                    <span>{i18n.t("dashboard.users.score")}</span>
                    <span style={{ color: nps.color, fontWeight: 700 }}>{nps.value}%</span>
                  </div>
                  <div className={classes.progressBar}>
                    <div
                      className={classes.progressFill}
                      style={{
                        width: `${nps.value}%`,
                        background: nps.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
